import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useLanguage } from '../context/LanguageContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const C = {
  teal:        '#0d9488',
  tealDark:    '#0f766e',
  tealLight:   '#f0fdfa',
  tealMid:     '#99f6e4',
  tealFoam:    '#ccfbf1',
  purple:      '#7c3aed',
  purpleLight: '#f5f3ff',
  purpleMid:   '#c4b5fd',
  ink:         '#0f172a',
  inkMid:      '#334155',
  inkLt:       '#64748b',
  border:      '#e2e8f0',
  white:       '#ffffff',
  cream:       '#f8fafc',
  red:         '#dc2626',
  redLight:    '#fef2f2',
  redMid:      '#fecaca',
  amber:       '#d97706',
  amberLight:  '#fffbeb',
  amberMid:    '#fde68a',
  green:       '#16a34a',
  greenLight:  '#f0fdf4',
  greenMid:    '#bbf7d0',
}

const DISPLAY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const BODY    = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function Icon({ d, size = 18, color = 'currentColor', sw = 1.7 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {d.split('|||').map((p, i) => <path key={i} d={p} />)}
    </svg>
  )
}

const IC = {
  bill:    'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z|||M14 2v6h6|||M16 13H8|||M16 17H8|||M10 9H8',
  card:    'M1 4h22v16H1z|||M1 10h22',
  check:   'M20 6L9 17l-5-5',
  clock:   'M12 2a10 10 0 100 20A10 10 0 0012 2z|||M12 6v6l4 2',
  alert:   'M12 9v4|||M12 17h.01|||M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  lock:    'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z|||M7 11V7a5 5 0 0110 0v4',
  dollar:  'M12 2v20|||M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6',
}

function StatusBadge({ status, t }) {
  const cfg = {
    paid:      { bg: C.greenLight, border: C.greenMid, color: C.green,  label: t('status_paid')      },
    pending:   { bg: C.amberLight, border: C.amberMid, color: C.amber,  label: t('status_pending')   },
    overdue:   { bg: C.redLight,   border: C.redMid,   color: C.red,    label: t('status_overdue')   },
    cancelled: { bg: C.cream,      border: C.border,   color: C.inkLt,  label: t('status_cancelled') },
  }
  const c = cfg[status] || cfg.pending
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99,
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.color, fontSize: 12, fontWeight: 700,
      fontFamily: BODY, letterSpacing: '0.02em',
    }}>
      {status === 'paid'    && <Icon d={IC.check} size={11} color={c.color} sw={2.5} />}
      {status === 'pending' && <Icon d={IC.clock} size={11} color={c.color} sw={2.5} />}
      {status === 'overdue' && <Icon d={IC.alert} size={11} color={c.color} sw={2.5} />}
      {c.label}
    </span>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function PatientBilling({ patientId }) {
  const { t } = useLanguage()
  const [invoices,  setInvoices]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [paying,    setPaying]    = useState(null)   // invoice id being processed
  const [error,     setError]     = useState(null)

  useEffect(() => {
    fetchInvoices()
    // If returning from Stripe, show a success note briefly
    const params = new URLSearchParams(window.location.search)
    if (params.get('payment') === 'success') {
      fetchInvoices()
    }
  }, [patientId])

  const fetchInvoices = () => {
    setLoading(true)
    axios.get(`${API}/billing/patient/${patientId}/invoices`)
      .then(r => setInvoices(r.data || []))
      .catch(() => setError('Could not load billing information.'))
      .finally(() => setLoading(false))
  }

  const handlePayNow = async (invoice) => {
    setPaying(invoice.id)
    try {
      const res = await axios.post(`${API}/billing/patient/create-payment`, {
        invoice_id: invoice.id,
        patient_id: patientId,
      })
      if (res.data?.url) {
        window.location.href = res.data.url
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Payment could not be initiated. Please try again.')
      setPaying(null)
    }
  }

  // Compute totals
  const unpaidInvoices = invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue')
  const totalDue       = unpaidInvoices.reduce((sum, inv) => sum + (inv.amount_cents || 0), 0) / 100

  if (loading) return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: `2px solid ${C.tealLight}`, borderTop: `2px solid ${C.teal}`, margin: '0 auto 14px', animation: 'spin .8s linear infinite' }} />
      <p style={{ fontFamily: DISPLAY, fontStyle: 'italic', color: C.inkLt, fontSize: 16 }}>Loading…</p>
    </div>
  )

  if (error) return (
    <div style={{ padding: 24, background: C.redLight, borderRadius: 14, border: `1px solid ${C.redMid}`, color: C.red, fontFamily: BODY }}>
      {error}
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'fadeUp .45s cubic-bezier(.22,1,.36,1) both' }}>

      {/* Header */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
          {t('nav_bills')}
        </p>
        <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 400, color: C.ink, lineHeight: 1.12 }}>
          {t('billing_title').split(' ')[0]} <em>{t('billing_title').split(' ').slice(1).join(' ')}</em>
        </h1>
        <p style={{ color: C.inkLt, fontSize: 14, marginTop: 6 }}>{t('billing_subtitle')}</p>
      </div>

      {/* Total Due Banner */}
      {unpaidInvoices.length > 0 && (
        <div style={{
          background: `linear-gradient(135deg, ${C.tealDark}, ${C.purple})`,
          borderRadius: 20, padding: '28px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 20, flexWrap: 'wrap',
          boxShadow: '0 8px 32px rgba(13,148,136,0.25)',
        }}>
          <div>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              {t('billing_total_due')}
            </p>
            <p style={{ fontFamily: DISPLAY, fontSize: 42, color: 'white', fontWeight: 400, lineHeight: 1 }}>
              ${totalDue.toFixed(2)}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 6 }}>
              {unpaidInvoices.length} {unpaidInvoices.length === 1 ? 'invoice' : 'invoices'} outstanding
            </p>
          </div>
          <Icon d={IC.dollar} size={52} color="rgba(255,255,255,0.2)" sw={1.5} />
        </div>
      )}

      {/* All paid state */}
      {invoices.length > 0 && unpaidInvoices.length === 0 && (
        <div style={{
          background: C.greenLight, border: `1.5px solid ${C.greenMid}`,
          borderRadius: 20, padding: '28px 32px',
          display: 'flex', alignItems: 'center', gap: 18,
        }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon d={IC.check} size={22} color="white" sw={2.5} />
          </div>
          <div>
            <p style={{ fontFamily: DISPLAY, fontSize: 20, color: C.green, fontStyle: 'italic' }}>{t('billing_all_paid')}</p>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {invoices.length === 0 ? (
        <div style={{ padding: '60px 40px', textAlign: 'center', background: C.white, borderRadius: 20, border: `1.5px dashed ${C.border}` }}>
          <Icon d={IC.bill} size={40} color={C.border} />
          <p style={{ fontFamily: DISPLAY, fontStyle: 'italic', color: C.inkLt, fontSize: 18, marginTop: 16 }}>{t('billing_no_bills')}</p>
        </div>
      ) : (
        <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid rgba(13,148,136,0.25)`, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 2fr 110px 110px 140px',
            gap: 12, padding: '14px 24px',
            background: C.cream, borderBottom: `1px solid ${C.border}`,
            fontSize: 11, fontWeight: 700, color: C.inkLt,
            textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: BODY,
          }}>
            <span>{t('billing_invoice')}</span>
            <span>{t('billing_description')}</span>
            <span>{t('billing_amount')}</span>
            <span>{t('billing_due')}</span>
            <span></span>
          </div>

          {/* Rows */}
          {invoices.map((inv, i) => {
            const isOverdue = inv.status === 'pending' && inv.due_date && new Date(inv.due_date) < new Date()
            const displayStatus = isOverdue ? 'overdue' : inv.status
            return (
              <div key={inv.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 2fr 110px 110px 140px',
                gap: 12, padding: '18px 24px', alignItems: 'center',
                borderBottom: i < invoices.length - 1 ? `1px solid ${C.border}` : 'none',
                background: displayStatus === 'overdue' ? 'rgba(220,38,38,0.02)' : 'white',
                fontFamily: BODY,
              }}>
                {/* ID */}
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.inkMid }}>#{String(inv.id).padStart(4, '0')}</span>
                  <div style={{ fontSize: 11, color: C.inkLt, marginTop: 2 }}>
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}
                  </div>
                </div>

                {/* Description */}
                <div style={{ fontSize: 14, color: C.inkMid, lineHeight: 1.4 }}>
                  {inv.description || '—'}
                </div>

                {/* Amount */}
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                  ${((inv.amount_cents || 0) / 100).toFixed(2)}
                </div>

                {/* Due date */}
                <div style={{ fontSize: 13, color: displayStatus === 'overdue' ? C.red : C.inkLt }}>
                  {inv.due_date || '—'}
                </div>

                {/* Status + action */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <StatusBadge status={displayStatus} t={t} />
                  {(displayStatus === 'pending' || displayStatus === 'overdue') && (
                    <button
                      onClick={() => handlePayNow(inv)}
                      disabled={paying === inv.id}
                      style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none', cursor: paying === inv.id ? 'wait' : 'pointer',
                        background: displayStatus === 'overdue'
                          ? `linear-gradient(135deg, ${C.red}, #b91c1c)`
                          : `linear-gradient(135deg, ${C.teal}, ${C.purple})`,
                        color: 'white', fontSize: 12, fontWeight: 700, fontFamily: BODY,
                        display: 'flex', alignItems: 'center', gap: 6,
                        opacity: paying === inv.id ? 0.7 : 1,
                        transition: 'opacity .15s, transform .15s',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 8px rgba(13,148,136,0.3)',
                      }}
                      onMouseEnter={e => { if (paying !== inv.id) e.currentTarget.style.transform = 'translateY(-1px)' }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)' }}
                    >
                      {paying === inv.id ? (
                        <>
                          <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                          Redirecting…
                        </>
                      ) : (
                        <>
                          <Icon d={IC.card} size={13} color="white" sw={2} />
                          {t('btn_pay_now')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Secure payment note */}
      {unpaidInvoices.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', background: C.cream, borderRadius: 12,
          border: `1px solid ${C.border}`, color: C.inkLt, fontSize: 13, fontFamily: BODY,
        }}>
          <Icon d={IC.lock} size={15} color={C.inkLt} />
          {t('billing_payment_processing')}
        </div>
      )}
    </div>
  )
}
